import React from "react";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { TableSkeleton } from "@ui-components/Skeleton/TableSkeleton";

/**
 * First load of a list page: the page's own shape (search bar + table) in skeleton,
 * inside the content area. It replaces the full-screen "Processing request..." overlay
 * on list pages: that overlay covered the whole app — menu and views included — for the
 * whole wait, so nothing could be done until a slow endpoint answered.
 */
export function ListPageSkeleton({ columns = 7, rows = 8 }: { columns?: number; rows?: number }) {
  return (
    <div className="p-4" data-testid="list-page-skeleton" aria-busy="true" aria-label="Loading">
      <div className="rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary p-6">
        <SkeletonBlock h={40} w="50%" className="mb-6" />
        <TableSkeleton columns={columns} rows={rows} />
      </div>
    </div>
  );
}

/**
 * A list is showing rows it already had while a newer answer loads. A thin bar, not a
 * spinner: the rows on screen are usable, and a louder signal would read as "wait".
 */
export function RefreshingIndicator({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div
      role="status"
      aria-label="Refreshing"
      data-testid="list-refreshing"
      className="pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden"
    >
      <div className="h-full w-1/3 animate-[list-refresh_1.2s_ease-in-out_infinite] rounded-full bg-semantic-border-brand-primary" />
    </div>
  );
}
